import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { CyberUser } from '../../types';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface CyberUsersState {
  cyberUsers: CyberUser[];
  currentCyberUser: CyberUser | null;
  loading: boolean;
  error: string | null;
}

const initialState: CyberUsersState = {
  cyberUsers: [],
  currentCyberUser: null,
  loading: false,
  error: null,
};

// Thunks asynchrones

/**
 * Récupérer tous les utilisateurs cyber
 */
export const fetchCyberUsers = createAsyncThunk(
  'cyberUsers/fetchAll',
  async ({ search, is_active }: { search?: string; is_active?: boolean } = {}, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (is_active !== undefined) params.append('is_active', is_active.toString());

      const response = await axios.get(`${API_URL}/cyber-users?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la récupération des utilisateurs cyber');
    }
  }
);

/**
 * Récupérer un utilisateur cyber par ID
 */
export const fetchCyberUserById = createAsyncThunk(
  'cyberUsers/fetchById',
  async (id: number, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/cyber-users/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la récupération de l\'utilisateur');
    }
  }
);

/**
 * Créer un nouvel utilisateur cyber
 */
export const createCyberUser = createAsyncThunk(
  'cyberUsers/create',
  async (
    userData: {
      username: string;
      email: string;
      password: string;
      nom: string;
      prenom: string;
      telephone?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/cyber-users`, userData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la création de l\'utilisateur');
    }
  }
);

/**
 * Modifier un utilisateur cyber
 */
export const updateCyberUser = createAsyncThunk(
  'cyberUsers/update',
  async (
    { id, updates }: { id: number; updates: Partial<CyberUser> },
    { rejectWithValue }
  ) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API_URL}/cyber-users/${id}`, updates, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la modification de l\'utilisateur');
    }
  }
);

/**
 * Supprimer un utilisateur cyber
 */
export const deleteCyberUser = createAsyncThunk(
  'cyberUsers/delete',
  async (id: number, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/cyber-users/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la suppression de l\'utilisateur');
    }
  }
);

/**
 * Activer/désactiver un compte cyber
 */
export const toggleActiveStatus = createAsyncThunk(
  'cyberUsers/toggleActive',
  async (id: number, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API_URL}/cyber-users/${id}/toggle-active`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return { id, is_active: response.data.data.is_active };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la modification du statut');
    }
  }
);

/**
 * Réinitialiser le mot de passe
 */
export const resetPassword = createAsyncThunk(
  'cyberUsers/resetPassword',
  async ({ id, new_password }: { id: number; new_password: string }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/cyber-users/${id}/reset-password`, { new_password }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la réinitialisation du mot de passe');
    }
  }
);

/**
 * Récupérer les clients d'un cyber
 */
export const fetchCyberClients = createAsyncThunk(
  'cyberUsers/fetchClients',
  async (id: number, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/cyber-users/${id}/clients`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la récupération des clients');
    }
  }
);

// Slice Redux
const cyberUsersSlice = createSlice({
  name: 'cyberUsers',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentCyberUser: (state) => {
      state.currentCyberUser = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch all
    builder.addCase(fetchCyberUsers.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchCyberUsers.fulfilled, (state, action) => {
      state.loading = false;
      state.cyberUsers = action.payload;
    });
    builder.addCase(fetchCyberUsers.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch by ID
    builder.addCase(fetchCyberUserById.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchCyberUserById.fulfilled, (state, action) => {
      state.loading = false;
      state.currentCyberUser = action.payload;
    });
    builder.addCase(fetchCyberUserById.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Create
    builder.addCase(createCyberUser.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(createCyberUser.fulfilled, (state, action) => {
      state.loading = false;
      state.cyberUsers = [...state.cyberUsers, action.payload];
    });
    builder.addCase(createCyberUser.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Update
    builder.addCase(updateCyberUser.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(updateCyberUser.fulfilled, (state, action) => {
      state.loading = false;
      const index = state.cyberUsers.findIndex((user) => user.id === action.payload.id);
      if (index !== -1) {
        state.cyberUsers[index] = action.payload;
      }
      if (state.currentCyberUser?.id === action.payload.id) {
        state.currentCyberUser = action.payload;
      }
    });
    builder.addCase(updateCyberUser.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Delete
    builder.addCase(deleteCyberUser.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(deleteCyberUser.fulfilled, (state, action) => {
      state.loading = false;
      state.cyberUsers = state.cyberUsers.filter((user) => user.id !== action.payload);
      if (state.currentCyberUser?.id === action.payload) {
        state.currentCyberUser = null;
      }
    });
    builder.addCase(deleteCyberUser.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Toggle active
    builder.addCase(toggleActiveStatus.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(toggleActiveStatus.fulfilled, (state, action) => {
      state.loading = false;
      const index = state.cyberUsers.findIndex((user) => user.id === action.payload.id);
      if (index !== -1) {
        state.cyberUsers[index].is_active = action.payload.is_active;
      }
      if (state.currentCyberUser?.id === action.payload.id) {
        state.currentCyberUser.is_active = action.payload.is_active;
      }
    });
    builder.addCase(toggleActiveStatus.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Reset password
    builder.addCase(resetPassword.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(resetPassword.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(resetPassword.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
  },
});

export const { clearError, clearCurrentCyberUser } = cyberUsersSlice.actions;
export default cyberUsersSlice.reducer;
